import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import './OperatorManagement.css';

interface OperatorDefaultRate {
  id: number;
  code: string;
  name: string;
  defaultCommissionRate: number;
}

interface UpdateOperatorRateRequest {
  operatorId: number;
  defaultCommissionRate: number;
  applyToExistingUsers: boolean;
}

const OperatorManagement: React.FC = () => {
  const [editingOperator, setEditingOperator] = useState<number | null>(null);
  const [tempRates, setTempRates] = useState<Record<number, number>>({});
  const [applyToExisting, setApplyToExisting] = useState<Record<number, boolean>>({});

  const queryClient = useQueryClient();

  // Fetch operator default rates
  const { data: operators, isLoading, error } = useQuery<OperatorDefaultRate[]>({
    queryKey: ['operator-default-rates'],
    queryFn: async () => {
      const response = await axios.get('/admin/commissions/operators');
      return response.data;
    }
  });

  // Update operator default rate mutation
  const updateRateMutation = useMutation({
    mutationFn: async (request: UpdateOperatorRateRequest) => {
      const response = await axios.put(`/admin/commissions/operators/${request.operatorId}`, request);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operator-default-rates'] });
      setEditingOperator(null);
      setTempRates({});
      setApplyToExisting({});
      alert('Operator default rate updated successfully!');
    },
    onError: (error: any) => {
      console.error('Error updating operator rate:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update operator rate';
      alert(`Error: ${errorMessage}`);
    }
  });

  const handleEditClick = (operatorId: number, currentRate: number) => {
    setEditingOperator(operatorId);
    setTempRates(prev => ({ ...prev, [operatorId]: currentRate * 100 })); // Convert to percentage
    setApplyToExisting(prev => ({ ...prev, [operatorId]: false }));
  };

  const handleCancelEdit = (operatorId: number) => {
    setEditingOperator(null);
    const newTempRates = { ...tempRates };
    delete newTempRates[operatorId];
    setTempRates(newTempRates);
    
    const newApplyToExisting = { ...applyToExisting };
    delete newApplyToExisting[operatorId];
    setApplyToExisting(newApplyToExisting);
  };

  const handleSaveClick = (operatorId: number) => {
    const rate = tempRates[operatorId];
    if (rate < 0 || rate > 100) {
      alert('Commission rate must be between 0% and 100%');
      return;
    }

    updateRateMutation.mutate({
      operatorId,
      defaultCommissionRate: rate / 100, // Convert percentage to decimal
      applyToExistingUsers: applyToExisting[operatorId] || false
    });
  };

  const handleRateChange = (operatorId: number, value: string) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setTempRates(prev => ({ ...prev, [operatorId]: parseFloat(value) || 0 }));
    }
  };

  const handleApplyToExistingChange = (operatorId: number, checked: boolean) => {
    setApplyToExisting(prev => ({ ...prev, [operatorId]: checked }));
  };

  if (isLoading) {
    return (
      <div className="operator-management">
        <div className="loading">Loading operator rates...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="operator-management">
        <div className="error">Error loading operator rates</div>
      </div>
    );
  }

  return (
    <div className="operator-management">
      <div className="header">
        <h1>Operator Management</h1>
        <p>Manage default commission rates for each operator</p>
      </div>

      <div className="operators-table-container">
        <table className="operators-table">
          <thead>
            <tr>
              <th>Operator Name</th>
              <th>Default Commission Rate</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {operators?.map((operator) => (
              <tr key={operator.id}>
                <td>
                  <span className="operator-name">{operator.name}</span>
                </td>
                <td>
                  {editingOperator === operator.id ? (
                    <div className="rate-edit">
                      <div className="rate-input-group">
                        <input
                          type="text"
                          value={tempRates[operator.id] || 0}
                          onChange={(e) => handleRateChange(operator.id, e.target.value)}
                          className="rate-input"
                          placeholder="5.0"
                        />
                        <span className="rate-suffix">%</span>
                      </div>
                      <div className="apply-existing-checkbox">
                        <label>
                          <input
                            type="checkbox"
                            checked={applyToExisting[operator.id] || false}
                            onChange={(e) => handleApplyToExistingChange(operator.id, e.target.checked)}
                          />
                          Apply to existing users
                        </label>
                      </div>
                    </div>
                  ) : (
                    <span className="rate-display">
                      {(operator.defaultCommissionRate * 100).toFixed(2)}%
                    </span>
                  )}
                </td>
                <td>
                  <div className="actions">
                    {editingOperator === operator.id ? (
                      <>
                        <button
                          onClick={() => handleSaveClick(operator.id)}
                          className="save-btn"
                          disabled={updateRateMutation.isPending}
                        >
                          {updateRateMutation.isPending ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => handleCancelEdit(operator.id)}
                          className="cancel-btn"
                          disabled={updateRateMutation.isPending}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleEditClick(operator.id, operator.defaultCommissionRate)}
                        className="edit-btn"
                      >
                        Edit Rate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {operators?.length === 0 && (
          <div className="no-operators">
            <p>No operators found</p>
          </div>
        )}
      </div>

      <div className="info-section">
        <div className="info-box">
          <h3>How Default Rates Work</h3>
          <ul>
            <li>Default rates are automatically assigned to new users when they are created</li>
            <li>You can customize individual user rates in the Commission Management section</li>
            <li>Checking "Apply to existing users" will update all current users to the new default rate</li>
            <li>Rates are entered as percentages (e.g., 5.0 for 5%)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default OperatorManagement;
